/**
 * CoachPlaybook - Shot Clock Component
 * Version: 0.5.146
 * Description: Digital shot clock display for play timing and countdown
 */

import React, { useMemo } from 'react';

const ShotClock = ({
  enabled = true,
  mode = 'duration',  // 'duration' | 'countdown'
  currentTime = 0,    // Current time in milliseconds
  countdownFrom = 24000,  // For countdown mode (24 seconds default)
  position = 'top-right',
  size = 'medium',
  showTenths = true,
  showWarning = true,
  warningThreshold = 5000,   // Yellow at 5 seconds
  criticalThreshold = 2000,  // Red at 2 seconds
  pulseOnLow = true,
  style: customStyle = {}
}) => {
  // Calculate display time
  const displayTime = useMemo(() => {
    if (mode === 'countdown') {
      return Math.max(0, countdownFrom - currentTime);
    }
    return currentTime;
  }, [mode, currentTime, countdownFrom]);
  
  // Format time for display
  const formattedTime = useMemo(() => {
    const seconds = displayTime / 1000;
    
    if (showTenths) {
      return seconds.toFixed(1);
    }
    return Math.ceil(seconds).toString();
  }, [displayTime, showTenths]);
  
  // Determine color state
  const colorState = useMemo(() => {
    if (mode !== 'countdown' || !showWarning) return 'normal';
    
    if (displayTime <= criticalThreshold) return 'critical';
    if (displayTime <= warningThreshold) return 'warning';
    return 'normal';
  }, [mode, displayTime, showWarning, warningThreshold, criticalThreshold]);
  
  // Should pulse
  const shouldPulse = pulseOnLow && colorState !== 'normal';
  
  // Size configurations
  const sizeConfig = {
    small: {
      width: '60px',
      height: '32px',
      fontSize: '18px',
      labelSize: '8px'
    },
    medium: {
      width: '80px',
      height: '44px',
      fontSize: '24px',
      labelSize: '10px'
    },
    large: {
      width: '120px',
      height: '64px',
      fontSize: '36px',
      labelSize: '12px'
    }
  };
  
  // Color configurations
  const colorConfig = {
    normal: {
      background: '#1f2937',
      text: '#ffffff',
      border: '#374151'
    },
    warning: {
      background: '#fbbf24',
      text: '#1f2937',
      border: '#f59e0b'
    },
    critical: {
      background: '#ef4444',
      text: '#ffffff',
      border: '#dc2626'
    }
  };
  
  // Position configurations
  const positionConfig = {
    'top-right': { top: '8px', right: '8px' },
    'top-left': { top: '8px', left: '8px' },
    'top-center': { top: '8px', left: '50%', transform: 'translateX(-50%)' },
    'bottom-right': { bottom: '8px', right: '8px' },
    'bottom-left': { bottom: '8px', left: '8px' },
    'bottom-center': { bottom: '8px', left: '50%', transform: 'translateX(-50%)' }
  };
  
  if (!enabled) return null;
  
  const currentSize = sizeConfig[size] || sizeConfig.medium;
  const currentColor = colorConfig[colorState];
  const currentPosition = positionConfig[position] || positionConfig['top-right'];
  
  return (
    <div
      style={{
        ...styles.container,
        ...currentPosition,
        ...customStyle,
        animation: shouldPulse ? 'shotClockPulse 0.5s infinite' : 'none'
      }}
    >
      <div
        style={{
          ...styles.clockBox,
          width: currentSize.width,
          height: currentSize.height,
          backgroundColor: currentColor.background,
          borderColor: currentColor.border
        }}
      >
        {/* Mode Label */}
        <div
          style={{
            ...styles.modeLabel,
            fontSize: currentSize.labelSize,
            color: currentColor.text,
            opacity: 0.7
          }}
        >
          {mode === 'countdown' ? 'SHOT CLOCK' : 'TIME'}
        </div>
        
        {/* Time Display */}
        <div
          style={{
            ...styles.timeDisplay,
            fontSize: currentSize.fontSize,
            color: currentColor.text
          }}
        >
          {formattedTime}
        </div>
      </div>
      
      {/* CSS Keyframes */}
      <style>
        {`
          @keyframes shotClockPulse {
            0%, 100% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.05); opacity: 0.8; }
          }
        `}
      </style>
    </div>
  );
};

// Scoreboard-style variant
export const ScoreboardClock = ({
  currentTime = 0,
  mode = 'countdown',
  countdownFrom = 24000,
  title = 'SHOT CLOCK',
  size = 'large',
  ...props
}) => {
  const displayTime = mode === 'countdown' 
    ? Math.max(0, countdownFrom - currentTime)
    : currentTime;
  
  const seconds = Math.ceil(displayTime / 1000);
  
  const sizeStyles = {
    small: { width: '80px', fontSize: '32px' },
    medium: { width: '120px', fontSize: '48px' },
    large: { width: '160px', fontSize: '64px' }
  };
  
  const currentSize = sizeStyles[size] || sizeStyles.medium;
  
  return (
    <div style={styles.scoreboardContainer}>
      <div style={styles.scoreboardTitle}>{title}</div>
      <div
        style={{
          ...styles.scoreboardDisplay,
          width: currentSize.width,
          fontSize: currentSize.fontSize
        }}
      >
        {seconds}
      </div>
    </div>
  );
};

// Game Clock variant (shows minutes:seconds)
export const GameClock = ({
  currentTime = 0,
  quarterLength = 480000, // 8 minutes in ms
  position = 'top-center',
  size = 'medium',
  ...props
}) => {
  const remainingTime = Math.max(0, quarterLength - currentTime);
  const minutes = Math.floor(remainingTime / 60000);
  const seconds = Math.floor((remainingTime % 60000) / 1000);
  const formattedTime = `${minutes}:${seconds.toString().padStart(2, '0')}`;
  
  const sizeConfig = {
    small: { fontSize: '16px', padding: '4px 8px' },
    medium: { fontSize: '20px', padding: '6px 12px' },
    large: { fontSize: '28px', padding: '8px 16px' }
  };
  
  const currentSize = sizeConfig[size] || sizeConfig.medium;
  const positionConfig = {
    'top-right': { top: '8px', right: '8px' },
    'top-left': { top: '8px', left: '8px' },
    'top-center': { top: '8px', left: '50%', transform: 'translateX(-50%)' }
  };
  
  return (
    <div
      style={{
        ...styles.gameClockContainer,
        ...positionConfig[position],
        ...currentSize
      }}
    >
      {formattedTime}
    </div>
  );
};

const styles = {
  container: {
    position: 'absolute',
    zIndex: 100
  },
  clockBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '8px',
    border: '2px solid',
    boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
    fontFamily: '"SF Mono", "Monaco", "Inconsolata", "Fira Code", monospace'
  },
  modeLabel: {
    textTransform: 'uppercase',
    letterSpacing: '1px',
    marginBottom: '-2px'
  },
  timeDisplay: {
    fontWeight: '700',
    lineHeight: '1',
    letterSpacing: '-1px'
  },
  scoreboardContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: '#000',
    padding: '16px 24px',
    borderRadius: '8px',
    border: '3px solid #333'
  },
  scoreboardTitle: {
    color: '#fbbf24',
    fontSize: '12px',
    fontWeight: '600',
    letterSpacing: '2px',
    marginBottom: '4px'
  },
  scoreboardDisplay: {
    color: '#ef4444',
    fontWeight: '700',
    fontFamily: '"SF Mono", "Monaco", monospace',
    textShadow: '0 0 10px rgba(239,68,68,0.5)'
  },
  gameClockContainer: {
    position: 'absolute',
    backgroundColor: '#1f2937',
    color: '#fff',
    borderRadius: '6px',
    fontFamily: '"SF Mono", "Monaco", monospace',
    fontWeight: '700',
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
    zIndex: 100
  }
};

export default ShotClock;
